import { useEffect, useState } from "react";
import { CircularProgress, Modal, Box, Card as MuiCard } from "@mui/material";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { API_URL } from "../../../config";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { FileText, FolderOpen, Close } from "@mui/icons-material";
import { CardContent, IconButton } from "@mui/material";

function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_URL}/projects/getprojects`);
        const data = await response.json();
        if (response.ok) {
          setProjects(data);
        }
      } catch (error) {
        toast.error("Error fetching projects", { description: error.message });
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const filteredProjects = projects.filter((project) =>
    project.projectTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <MDBox>
      <MDBox mt={3} textAlign="center">
        <MDTypography variant="button" color="text">
          <MDTypography
            component={Link}
            to="/landingpage"
            variant="button"
            color="info"
            fontWeight="medium"
            textGradient
          >
            Back
          </MDTypography>
        </MDTypography>
      </MDBox>
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        minHeight="100vh"
        px={3}
        py={5}
      >
        <MDBox
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="success"
          p={3}
          textAlign="center"
          width="100%"
          maxWidth="800px"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white">
            FAST Research Projects
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Search and browse previous and ongoing available projects
          </MDTypography>
        </MDBox>

        <MDBox width="100%" maxWidth="800px" mt={3}>
          <MDInput
            type="text"
            label="Search Projects"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="standard"
            fullWidth
          />
        </MDBox>

        <MDBox width="100%" maxWidth="800px" mt={3}>
          {loading ? (
            <CircularProgress
              size={32}
              color="inherit"
              sx={{ display: "block", margin: "auto" }}
            />
          ) : (
            <MDBox display="flex" flexDirection="column" gap={2} width="100%">
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project) => (
                  <Card
                    key={project.id}
                    sx={{
                      cursor: "pointer",
                      p: 2,
                      transition: "0.3s",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                    onClick={() => setSelectedProject(project)}
                  >
                    <MDTypography variant="h6" fontWeight="medium">
                      {project.projectTitle}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      {project.projectDescription.substring(0, 100)}...
                    </MDTypography>
                  </Card>
                ))
              ) : (
                <MDTypography variant="body2" textAlign="center" color="text">
                  No projects found.
                </MDTypography>
              )}
            </MDBox>
          )}
        </MDBox>

        {/* Modal for Project Details */}
        <Modal
          open={!!selectedProject}
          onClose={() => setSelectedProject(null)}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              maxWidth: "1000px",
              height: "90%",
              bgcolor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              p: 4,
            }}
          >
            {selectedProject && (
              <>
                {/* Close Button */}
                <IconButton
                  sx={{ position: "absolute", top: 10, right: 10 }}
                  onClick={() => setSelectedProject(null)}
                >
                  <Close />
                </IconButton>

                <MDTypography variant="h5" fontWeight="medium" gutterBottom>
                  {selectedProject.projectTitle}
                </MDTypography>
                <MDTypography variant="body1" color="text" mb={2}>
                  {selectedProject.projectDescription}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <strong>Academic Year:</strong> {selectedProject.academicYear}
                </MDTypography>

                {/* Links to Project Documents */}
                <MDBox display="flex" gap={3} mt={3}>
                  {selectedProject.conceptPaper && (
                    <MDTypography
                      component="a"
                      href={selectedProject.conceptPaper}
                      target="_blank"
                      color="info"
                    >
                      📄 View Concept Paper
                    </MDTypography>
                  )}
                  {selectedProject.proposal && (
                    <MDTypography
                      component="a"
                      href={selectedProject.proposal}
                      target="_blank"
                      color="success"
                    >
                      📑 View Proposal
                    </MDTypography>
                  )}
                  {selectedProject.report && (
                    <MDTypography
                      component="a"
                      href={selectedProject.report}
                      target="_blank"
                      color="secondary"
                    >
                      📊 View Report
                    </MDTypography>
                  )}
                </MDBox>
              </>
            )}
          </Box>
        </Modal>
      </MDBox>
    </MDBox>
  );
}

export default ProjectList;
